import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Modal,
  Layout,
  Text,
  Padding,
  AnchorLink,
  Container,
  Icon,
  IconList,
} from "@kidslivesafe/sc-react";
import { selectMemberData } from "../../features/authentication/authenticationSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  createCustomerRedisTokenAsync,
  sendMemberEventAsync,
} from "../../features/admin/adminSlice";
import { selectBrandInfo } from "../../features/brand/brandSlice";
import { useNavigate } from "react-router-dom";
import { formatPhoneNumber } from "../../utils";
import {
  getSubscriptionDetailsAsync,
  retryPaymentAsync,
  selectPrimarySubscription,
  setShowSecondaryAccelerate,
} from "../../features/subscriptions/subscriptionSlice";
import { useCurrentBreakpoint } from "@kidslivesafe/sc-foundation";

const SecondaryAccelerateModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const brandInfo = useAppSelector(selectBrandInfo);
  const subscriptionDetails = useAppSelector(selectPrimarySubscription);
  const { billingDate } = useAppSelector(selectMemberData);
  const [retryClicked, setRetryClicked] = useState<boolean>(false);
  const [retrySuccess, setRetrySuccess] = useState<boolean>(false);
  const [showAccelerateModal, setShowAccelerateModal] =
    useState<boolean>(false);
  const [retryLoading, setRetryLoading] = useState<boolean>(false);

  const hasSentModalDisplayEvent = useRef(false);
  const hasSentAccelerateConfirmationEvent = useRef(false);
  const currentBreakpoint = useCurrentBreakpoint();

  useEffect(() => {
    if (showAccelerateModal && retryClicked && retrySuccess) {
      //modal charge payment successful
      dispatch(
        sendMemberEventAsync({
          EventCategory: "Secondary Accelerate Modal",
          EventAction: "Successful Purchase",
        }),
      );
      if (!hasSentAccelerateConfirmationEvent.current) {
        hasSentAccelerateConfirmationEvent.current = true;
        //Accelerate modal only shown once
      }
    } else if (retryClicked && !retrySuccess) {
      //modal charge payment failed
      dispatch(
        sendMemberEventAsync({
          EventCategory: "Secondary Accelerate Modal",
          EventAction: "Failed Purchase",
        }),
      );
    } else if (subscriptionDetails?.showSecondaryAccelerate) {
      if (!hasSentModalDisplayEvent.current && !retryClicked && !retrySuccess) {
        hasSentModalDisplayEvent.current = true;
      }
      dispatch(
        sendMemberEventAsync({
          EventCategory: "Secondary Accelerate Modal",
          EventAction: "Modal Display",
        }),
      );
      setShowAccelerateModal(true);
    } else {
      setShowAccelerateModal(false);
    }
  }, [
    retryClicked,
    retrySuccess,
    subscriptionDetails?.showSecondaryAccelerate,
  ]);

  const handleRetryPayment = async () => {
    setRetryLoading(true);
    try {
      dispatch(
        sendMemberEventAsync({
          EventCategory: "Secondary Accelerate Modal",
          EventAction: "Confirm Offer",
        }),
      );

      const email = "accelerate";
      const IgnoreBillingDateCheck = true;

      const resultAction = await dispatch(
        retryPaymentAsync({ email, IgnoreBillingDateCheck }),
      );
      if (retryPaymentAsync.fulfilled.match(resultAction)) {
        setRetryClicked(true);
        setRetrySuccess(true);
        setRetryLoading(false);
        // get subscription details
        await dispatch(getSubscriptionDetailsAsync());

        setTimeout(() => {
          navigate("/");
        }, 0);
      } else {
        setRetryClicked(true);
        setRetrySuccess(false);
        setRetryLoading(false);
      }
    } catch (error) {
      console.error("Retry failed:", error);
    }
  };

  const handleExternalNavigation = async () => {
    const createCustomerRedisTokenAsyncResponse = await dispatch(
      createCustomerRedisTokenAsync(),
    );

    if (
      createCustomerRedisTokenAsync.fulfilled.match(
        createCustomerRedisTokenAsyncResponse,
      )
    ) {
      const uuid = createCustomerRedisTokenAsyncResponse.payload.token;
      window.location.href = brandInfo.homeUrl + "/fix-payment?uuid=" + uuid;
    }
  };
  return (
    <>
      <Modal
        open={showAccelerateModal}
        setOpen={() => {
          dispatch(setShowSecondaryAccelerate(false));
          setShowAccelerateModal(false);
        }}
        size="fit-content"
        xClose={false}
        outSideClose={false}
        scrollable={false}
      >
        <Padding
          space="sm"
          top="xxs"
          breakpointStyles={{ mobile: { space: "none" } }}
        >
          <Layout
            type="flex"
            flexProps={{
              direction: "column",
              gap: "sm",
              alignItems: "center",
            }}
            breakpointStyles={{
              desktop: {
                type: "flex",
                flexProps: {
                  direction: "column",
                  gap: "md",
                  alignItems: "center",
                },
              },
            }}
          >
            {!retryClicked &&
            !retrySuccess &&
            subscriptionDetails?.showSecondaryAccelerate ? (
              <>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "sm",
                  }}
                  breakpointStyles={{
                    desktop: {
                      type: "flex",
                      flexProps: {
                        direction: "column",
                        gap: "xl",
                        alignItems: "center",
                      },
                    },
                  }}
                >
                  <Container altColor="alt" bodyBorderRadius>
                    <Padding space="xxs" left="xs" right="xs">
                      <Text textAlign="center" size="sm" fontWeight="semibold">
                        Premium Feature
                      </Text>
                    </Padding>
                  </Container>

                  <div>
                    <Text textAlign="center" tag="h2">
                      Trial Limit Reached
                    </Text>
                  </div>
                  <Text textAlign="center" size="lg" fontWeight="semibold">
                    Upgrade to access up to 100 Property, Phone, and Social
                    Media reports.
                  </Text>
                  <Container bodyBorderRadius border borderThickness={2}>
                    <Padding space="sm" top="md" bottom="md">
                      <Layout
                        type="flex"
                        flexProps={{
                          gap: "xs",
                          alignItems: "flex-start",
                          direction: "column",
                        }}
                      >
                        <Layout
                          type="flex"
                          flexProps={{
                            gap: "xs",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <div>
                            <Icon name={IconList.IconHouse} size={40} />
                          </div>

                          <Layout
                            type="flex"
                            flexProps={{ gap: "xxs", direction: "column" }}
                          >
                            <Text fontWeight="semibold" size="sm">
                              Address Reports
                            </Text>
                            <Text size="xs">
                              Confirm property ownership and address history
                            </Text>
                          </Layout>
                        </Layout>
                        <Layout
                          type="flex"
                          flexProps={{
                            gap: "xs",
                            alignItems: "center",
                          }}
                        >
                          <div>
                            <Icon name={IconList.IconPhone} size={40} />
                          </div>

                          <Layout
                            type="flex"
                            flexProps={{ gap: "xxs", direction: "column" }}
                          >
                            <Text fontWeight="semibold" size="sm">
                              Phone Reports
                            </Text>
                            <Text size="xs">
                              Cross-check phone numbers and associated contacts
                            </Text>
                          </Layout>
                        </Layout>
                        <Layout
                          type="flex"
                          flexProps={{
                            gap: "xs",
                            alignItems: "center",
                          }}
                        >
                          <div>
                            <Icon name={IconList.IconSocial} size={40} />
                          </div>

                          <Layout
                            type="flex"
                            flexProps={{ gap: "xxs", direction: "column" }}
                          >
                            <Text fontWeight="semibold" size="sm">
                              Social Media
                            </Text>
                            <Text size="xs">
                              Discover linked social profiles and online
                              presences
                            </Text>
                          </Layout>
                        </Layout>
                      </Layout>
                    </Padding>
                  </Container>
                </Layout>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "column",
                    gap: "md",
                    alignItems: "center",
                  }}
                  fillHorizontal
                >
                  <Text textAlign="center" size="sm">
                    Continue your research with full access.
                  </Text>
                  <Button
                    buttonText={
                      <Text size="base" textWrap="balance">
                        Yes, Start My Full Membership Today
                      </Text>
                    }
                    fill={currentBreakpoint !== "desktop" ? "default" : "fill"}
                    size={currentBreakpoint !== "desktop" ? "icon" : "md"}
                    handleClick={handleRetryPayment}
                    clicked={retryClicked}
                    loading={retryLoading}
                  />

                  <Text textAlign="center" size="xs" fontWeight="light">
                    ${subscriptionDetails?.price} Monthly Billing begins
                    immediately, cancel anytime
                  </Text>

                  <AnchorLink
                    handleClick={() => {
                      dispatch(
                        sendMemberEventAsync({
                          EventCategory: "Secondary Accelerate Modal",
                          EventAction: "Decline Accelerate",
                        }),
                      );
                      dispatch(setShowSecondaryAccelerate(false));
                      setShowAccelerateModal(false);
                    }}
                    stayUnderline
                    role="button"
                    size="sm"
                  >
                    No, I want to continue my trial
                  </AnchorLink>
                </Layout>

                <Text textAlign="center" size="sm">
                  Closing this window will return you to your trial. For
                  questions, contact support at{" "}
                  <AnchorLink
                    inline
                    phoneNumber
                    hoverUnderline
                    href={brandInfo.phoneNumber}
                  >
                    {formatPhoneNumber(brandInfo.phoneNumber)}
                  </AnchorLink>
                  .
                </Text>
              </>
            ) : retryClicked && !retrySuccess ? (
              <>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "column",
                    gap: "sm",
                  }}
                >
                  <div>
                    <Text textAlign="center" tag="h2" fontWeight="semibold">
                      We've encountered a problem!{" "}
                    </Text>
                  </div>
                  <Text textAlign="center" size="sm">
                    We couldn’t process your payment, possibly due to an expired
                    or declined card.
                  </Text>
                  <Text textAlign="center" size="sm">
                    Please update your payment details to maintain access to
                    your full subscription.
                  </Text>
                </Layout>
                <Button
                  buttonText="Update Payment Method"
                  handleClick={handleExternalNavigation}
                  size="md"
                  fill="fill"
                />
                <Text textAlign="center" size="sm">
                  For questions please contact member support at{" "}
                  <AnchorLink
                    inline
                    phoneNumber
                    hoverUnderline
                    href={brandInfo.phoneNumber}
                  >
                    {formatPhoneNumber(brandInfo.phoneNumber)}
                  </AnchorLink>
                  .
                </Text>
              </>
            ) : retryClicked && retrySuccess && showAccelerateModal ? (
              <>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "column",
                    gap: "lg",
                  }}
                >
                  <div>
                    <Text textAlign="center" tag="h2" fontWeight="semibold">
                      You're All Set!{" "}
                    </Text>
                    <Padding top="lg">
                      <Layout
                        type="flex"
                        flexProps={{ direction: "column", gap: "lg" }}
                      >
                        <Text textAlign="center" size="sm">
                          Your subscription is now active, and you've unlocked
                          full access to {brandInfo.name}.
                        </Text>
                        <Text textAlign="center" size="sm">
                          Your subscription renews monthly with your next
                          billing date on {billingDate}.
                        </Text>
                      </Layout>
                    </Padding>
                  </div>
                </Layout>
                <Button
                  buttonText="Go to Dashboard"
                  handleClick={() => {
                    dispatch(setShowSecondaryAccelerate(false));
                    setShowAccelerateModal(false);
                  }}
                  color="success"
                  cursor="pointer"
                />
              </>
            ) : null}
          </Layout>
        </Padding>
      </Modal>
    </>
  );
};
export default SecondaryAccelerateModal;
